import React, { useState } from 'react';

import {
  DataGrid,
  PAGE_SIZE_OPTIONS,
  SearchInput,
  useDataGridSelectionModel,
  useToast,
} from '@dutchie/backoffice-ui-library';

import {
  SearchLspBrandsDocument,
  useBulkUpdateLspBrandSettingsMutation,
} from 'src/apps/brand-connect/api/generated/graphql';

import { ROW_HEIGHT, SCROLLBAR_SIZE } from '../shared/constants';
import { useServerPaginatedBrandData } from '../shared/hooks/use-server-paginated-brand-data';
import {
  DataGridContainer,
  GridAndSearchContainer,
  Heading,
  Page,
  SearchContainer,
} from '../shared/styled';

import { ConfirmModal, SideContent } from './components';
import { BULK_ACTIONS, BulkAction } from './constants';
import { useDataShareBulkUpdateLoading } from './context/data-share-update-loading-context';
import { ContentContainer, MainContent } from './styled';
import { useColumns } from './use-columns';

export const Connections = () => {
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);
  const { showSuccess, showError } = useToast();

  const {
    handleSearchChange,
    loading: brandsLoading,
    paginationModel,
    rows,
    setPaginationModel,
    setSortModel,
    sortModel,
    totalCount,
  } = useServerPaginatedBrandData();

  const { dataSharingBulkUpdateLoading, setDataSharingBulkUpdateLoading } =
    useDataShareBulkUpdateLoading();

  const loading = brandsLoading || dataSharingBulkUpdateLoading;

  const { clearSelection, selectionModel, handleSelectionChange } =
    useDataGridSelectionModel();

  const columns = useColumns();

  const [bulkUpdateLspBrandSettings] = useBulkUpdateLspBrandSettingsMutation({
    refetchQueries: [SearchLspBrandsDocument],
    awaitRefetchQueries: true,
  });

  const handleBulkActionClick = (value: string) => {
    // The strict enum type gets lost when the value passes through the data grid;
    // however, this is still type safe.
    // We pass only BulkAction values as bulk action option values, so we know that's what we're getting back.
    setPendingAction(value as BulkAction);
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;
    try {
      setDataSharingBulkUpdateLoading(true);
      const targetConnectionStatus = pendingAction === BulkAction.Enable;
      setPendingAction(null);

      await bulkUpdateLspBrandSettings({
        variables: {
          updates: selectionModel.map((id) => ({
            brandId: String(id),
            settings: {
              dataSharingEnabled: targetConnectionStatus,
            },
          })),
        },
      });
      showSuccess({
        message: `Data sharing status successfully changed for ${selectionModel.length} brand${selectionModel.length > 1 ? 's' : ''}.`,
      });
    } catch (e) {
      console.error(e);
      showError({
        message: `Failed to update data sharing status. Please try again.`,
      });
    } finally {
      setDataSharingBulkUpdateLoading(false);
      clearSelection();
    }
  };

  return (
    <>
      <Page>
        <Heading subheader="Manage your connection status with brands that are on the Dutchie Brands Network.">
          Connections
        </Heading>
        <ContentContainer>
          <MainContent>
            <GridAndSearchContainer $responsiveHeight>
              <SearchContainer>
                <SearchInput
                  onSearchChange={handleSearchChange}
                  placeholder="Search brands by name..."
                />
              </SearchContainer>
              <DataGridContainer>
                <DataGrid
                  bulkActionOptions={BULK_ACTIONS}
                  onBulkActionClick={handleBulkActionClick}
                  checkboxSelection
                  columns={columns}
                  loading={loading}
                  onPaginationModelChange={setPaginationModel}
                  onSortModelChange={setSortModel}
                  paddedFooter
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationMode="server"
                  paginationModel={paginationModel}
                  rowCount={totalCount}
                  rowHeight={ROW_HEIGHT}
                  rows={rows}
                  scrollbarSize={SCROLLBAR_SIZE}
                  sortingMode="server"
                  sortModel={sortModel}
                  selectionModel={selectionModel}
                  onSelectionChange={handleSelectionChange}
                />
              </DataGridContainer>
            </GridAndSearchContainer>
          </MainContent>
          <SideContent />
        </ContentContainer>
      </Page>
      {pendingAction && (
        <ConfirmModal
          onClose={() => setPendingAction(null)}
          onConfirm={handleConfirm}
          pendingAction={pendingAction}
        />
      )}
    </>
  );
};
